// THE LEVIATHAN on screen (sim/bosses/leviathan.ts): the extraction pursuer, island-sized.
//
// Frame budget (James's #1): the whole creature is TWO skinned meshes, one per GLB, each split
// only by material, so it costs ~6 draws however much of it is on screen.
//   - body:     every node of leviathan_body.glb becomes a bone; its meshes are baked into one
//               geometry per material (paint / glow_red / glow_fire), each vertex rigidly bound to
//               its node's bone. Eyes, visors, jaw, maw, turrets turn by bone; a part shot off
//               collapses its bone to scale 0 (no extra meshes, no state changes in the draw list).
//   - tendrils: leviathan_tendril.glb instanced five times the same way: 5 copies of its bone
//               chain in one geometry (paint / glow_elem / glow_gold). The joint rotations come
//               from the sim's own poseLimbs(), so the limb you see is the limb the server hits.
// No shadows (a 90 m caster would cost a second pass of 68k triangles for a shadow off the
// islet), haze-capped materials so it reads as atmospheric distance against the sky, and no LOD:
// it is never nearer than ~40 m nor farther than the island, so one 68k mesh is the right level.
//
// What reads, from the platform:
//   - the rise: it climbs out of the cloud sea beside the islet over five seconds (G5 arrival)
//   - the stare: the body swings to face the posse; the porthole eyes glow red and track the deck
//   - tells: eyes flare for every tell; rocket drums spin up; the jaw drops (furnace gold);
//     a tendril rears and a strike ring locks on the deck
//   - hits: the visor snaps shut over a shot eye; turrets / barnacles / limbs pop off; the socket
//     glows gold for its wound window; all five limbs gone → the dome's ember cracks open

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { BossBeat, BossKind } from '../../../../sim/bosses/types';
import {
  EYES, JOINTS, LAtk, LBit, LEVI_SCALE, LKind, LPh, LeviEv, LeviNet, LeviView, N_BARN, N_LIMBS, N_TURRETS, SEG_OFF, SOCKETS,
  barnAlive, jawOpen, leviRaycast, limbAlive, newLeviHit, newLeviView, partLabel, poseLimbs, toWorld, turretAlive,
} from '../../../../sim/bosses/leviathan';
import { withHaze } from '../../materials';
import type { HudBoss, HudObjective } from '../../contracts';
import { DANGER, Decal, groundDecal } from './decals';
import type { BossAim, BossClient, BossClientFactory, BossCtx, BossFrame } from './types';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const BODY_URL = BASE + 'assets/run/leviathan_body.glb';
const LIMB_URL = BASE + 'assets/run/leviathan_tendril.glb';
/** each severed limb takes the skirt panel over its socket with it */
const LIMB_PANEL = [4, 6, 8, 1, 3];
const SEG_NAMES = Array.from({ length: JOINTS }, (_, k) => `seg_${String(k).padStart(2, '0')}`);

/** The texkit atlases ship at 2048²; six of them would hold ~130 MB of VRAM for one creature that
 *  is never nearer than ~40 m. Halved (off the main thread, before the first upload) they cost ~32 MB. */
const TEX_MAX = 1024;
async function shrinkTextures(scenes: THREE.Object3D[], max: number) {
  const seen = new Set<THREE.Texture>();
  for (const sc of scenes) sc.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
    if (!m || !(o as THREE.Mesh).isMesh) return;
    for (const t of [m.map, m.normalMap, m.roughnessMap, m.metalnessMap, m.aoMap, m.emissiveMap]) if (t) seen.add(t);
  });
  await Promise.all([...seen].map(async (t) => {
    const img = t.image as ImageBitmap | HTMLImageElement | undefined;
    if (!img || img.width <= max || typeof createImageBitmap !== 'function') return;
    const k = max / Math.max(img.width, img.height);
    try {
      const small = await createImageBitmap(img, { resizeWidth: Math.round(img.width * k), resizeHeight: Math.round(img.height * k), resizeQuality: 'high' });
      if ('close' in img) img.close();
      t.image = small; t.needsUpdate = true;
    } catch { /* keep the full-size atlas */ }
  }));
}

interface Rig { bones: THREE.Bone[]; byName: Map<string, THREE.Bone>[]; meshes: THREE.SkinnedMesh[] }

/** Bake a GLB scene into skinned meshes (one per material): every node a bone, every vertex rigid
 *  on its node's bone, `copies` copies of the whole tree sharing one skeleton. */
function bake(scene: THREE.Object3D, copies: number, parent: THREE.Object3D, prep: (m: THREE.Material) => THREE.Material): Rig {
  scene.updateMatrixWorld(true);
  const nodes: THREE.Object3D[] = [];
  scene.traverse((o) => { if (o !== scene) nodes.push(o); });
  const index = new Map<THREE.Object3D, number>();
  nodes.forEach((n, i) => index.set(n, i));
  const bones: THREE.Bone[] = [];
  const byName: Map<string, THREE.Bone>[] = [];
  for (let c = 0; c < copies; c++) {
    const names = new Map<string, THREE.Bone>();
    const local: THREE.Bone[] = [];
    for (const n of nodes) {
      const b = new THREE.Bone();
      b.name = n.name;
      b.position.copy(n.position); b.quaternion.copy(n.quaternion); b.scale.copy(n.scale);
      local.push(b);
      if (n.name && !names.has(n.name)) names.set(n.name, b);
    }
    nodes.forEach((n, i) => {
      const pi = n.parent ? index.get(n.parent) : undefined;
      (pi === undefined ? parent : local[pi]).add(local[i]);
    });
    bones.push(...local);
    byName.push(names);
  }
  // geometry per material, model space (relative to the GLB scene root)
  const inv = new THREE.Matrix4().copy(scene.matrixWorld).invert();
  const groups = new Map<THREE.Material, { pos: number[]; nrm: number[]; uv: number[]; skin: number[]; idx: number[] }>();
  const m4 = new THREE.Matrix4(), n3 = new THREE.Matrix3(), v = new THREE.Vector3();
  for (const n of nodes) {
    const mesh = n as THREE.Mesh;
    if (!mesh.isMesh) continue;
    const mat = mesh.material as THREE.Material;
    let gr = groups.get(mat);
    if (!gr) { gr = { pos: [], nrm: [], uv: [], skin: [], idx: [] }; groups.set(mat, gr); }
    const geo = mesh.geometry;
    const P = geo.getAttribute('position'), N = geo.getAttribute('normal'), U = geo.getAttribute('uv');
    m4.multiplyMatrices(inv, mesh.matrixWorld);
    n3.getNormalMatrix(m4);
    const ni = index.get(n)!;
    const ix = geo.getIndex();
    for (let c = 0; c < copies; c++) {
      const base = gr.pos.length / 3, bone = c * nodes.length + ni;
      for (let i = 0; i < P.count; i++) {
        v.fromBufferAttribute(P, i).applyMatrix4(m4); gr.pos.push(v.x, v.y, v.z);
        if (N) { v.fromBufferAttribute(N, i).applyMatrix3(n3).normalize(); gr.nrm.push(v.x, v.y, v.z); } else gr.nrm.push(0, 1, 0);
        if (U) gr.uv.push(U.getX(i), U.getY(i)); else gr.uv.push(0, 0);
        gr.skin.push(bone);
      }
      if (ix) for (let i = 0; i < ix.count; i++) gr.idx.push(base + ix.getX(i));
      else for (let i = 0; i < P.count; i++) gr.idx.push(base + i);
    }
  }
  parent.updateMatrixWorld(true);
  const skeleton = new THREE.Skeleton(bones);
  const meshes: THREE.SkinnedMesh[] = [];
  for (const [mat, gr] of groups) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(gr.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(gr.nrm, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(gr.uv, 2));
    const si = new Uint16Array(gr.skin.length * 4), sw = new Float32Array(gr.skin.length * 4);
    for (let i = 0; i < gr.skin.length; i++) { si[i * 4] = gr.skin[i]; sw[i * 4] = 1; }
    g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(si, 4));
    g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(sw, 4));
    g.setIndex(gr.pos.length / 3 > 65535 ? new THREE.Uint32BufferAttribute(gr.idx, 1) : new THREE.Uint16BufferAttribute(gr.idx, 1));
    const sm = new THREE.SkinnedMesh(g, prep(mat));
    sm.frustumCulled = false; sm.castShadow = false; sm.receiveShadow = true;
    parent.add(sm);
    sm.bind(skeleton);
    meshes.push(sm);
  }
  return { bones, byName, meshes };
}

class LeviClient implements BossClient {
  readonly kind = BossKind.Leviathan;
  readonly title = 'THE LEVIATHAN';
  readonly sub = 'IT CAME FOR THE TRAIN';
  private group = new THREE.Group();
  private model = new THREE.Group();
  private body: Rig | null = null;
  private limbs: Rig | null = null;
  private rest = new Map<THREE.Bone, THREE.Quaternion>();
  private mats: { red: THREE.MeshStandardMaterial[]; fire: THREE.MeshStandardMaterial[]; gold: THREE.MeshStandardMaterial[]; elem: THREE.MeshStandardMaterial[] } = { red: [], fire: [], gold: [], elem: [] };
  private view: LeviView = newLeviView();
  private quats = new Float64Array(N_LIMBS * JOINTS * 4);
  private strike: Decal;
  private disposables: { dispose(): void }[] = [];
  private age = 0; // client tendril clock (eases onto the server's)
  private lastSeen = -1;
  private deadAt = -1;
  private sinkFrom = 0;
  private live = false;
  private hit = newLeviHit();
  private q1 = new THREE.Quaternion(); private q2 = new THREE.Quaternion();
  private v1 = new THREE.Vector3(); private v2 = new THREE.Vector3();
  private w1 = { x: 0, y: 0, z: 0 };
  private lastNet: LeviNet | null = null;

  constructor(private ctx: BossCtx) {
    this.group.name = 'leviathan';
    this.group.visible = false;
    this.model.scale.setScalar(LEVI_SCALE);
    this.group.add(this.model);
    ctx.scene.add(this.group);
    this.strike = groundDecal(ctx.scene, ctx.time, 3, DANGER);
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    Promise.all([loader.loadAsync(BODY_URL), loader.loadAsync(LIMB_URL)])
      .then(async ([b, l]) => { await shrinkTextures([b.scene, l.scene], TEX_MAX); this.onModels(b.scene, l.scene); ctx.prewarm?.([this.group]); })
      .catch((e) => console.warn('[leviathan] models failed', e));
  }

  private onModels(bodyScene: THREE.Object3D, limbScene: THREE.Object3D) {
    const made = new Map<THREE.Material, THREE.Material>();
    const prep = (m: THREE.Material) => {
      let o = made.get(m);
      if (o) return o;
      const sm = m as THREE.MeshStandardMaterial;
      // the hull keeps more of its form through the haze than a robot would: it must read against the sky
      o = withHaze(sm, sm.name === 'paint' ? 0.42 : 0.18);
      made.set(m, o);
      this.disposables.push(o);
      if (sm.name === 'glow_red') this.mats.red.push(sm);
      else if (sm.name === 'glow_fire') this.mats.fire.push(sm);
      else if (sm.name === 'glow_gold') this.mats.gold.push(sm);
      else if (sm.name === 'glow_elem') this.mats.elem.push(sm);
      return o;
    };
    this.body = bake(bodyScene, 1, this.model, prep);
    this.limbs = bake(limbScene, N_LIMBS, this.model, prep);
    for (const r of [this.body, this.limbs]) {
      for (const b of r.bones) this.rest.set(b, b.quaternion.clone());
      for (const m of r.meshes) this.disposables.push(m.geometry);
    }
    // the ember cracks only open when it is skinned
    for (let i = 0; i < 4; i++) this.bone(`fx_wound_${i}`)?.scale.setScalar(0);
    bodyScene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); });
    limbScene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); });
  }

  private bone(name: string) { return this.body?.byName[0].get(name) ?? null; }
  private limbBone(i: number, name: string) { return this.limbs?.byName[i].get(name) ?? null; }
  private restQ(b: THREE.Bone) { return this.rest.get(b) ?? b.quaternion; }

  /** the shared pose (sim volumes + limb kinematics) from the net payload and the interpolated body */
  private fillView(f: BossFrame, net: LeviNet) {
    const e = f.primary!;
    const v = this.view;
    v.x = e.x; v.y = e.y; v.z = e.z; v.yaw = e.yaw;
    v.age = this.age;
    v.parts = net.parts; v.lids = net.lids; v.socks = net.socks;
    v.skinned = (net.phase & LBit.Skinned) !== 0;
    v.atk = net.atk; v.ph = net.phase & LBit.PhMask; v.prog = net.prog;
    v.limb = net.limb; v.tx = net.tx; v.ty = net.ty; v.tz = net.tz;
    return v;
  }

  update(f: BossFrame | null, time: number, dt: number) {
    this.strike.mesh.visible = false;
    const net = f?.net as LeviNet | undefined;
    if (!f || !net || !f.primary) {
      // dead: it sinks back into the cloud sea, burning; gone (the train left): it sinks quietly
      if (this.live && this.lastSeen >= 0) {
        const k = (time - (this.deadAt >= 0 ? this.deadAt : this.lastSeen)) / (this.deadAt >= 0 ? 6 : 3);
        if (k < 1) {
          this.group.position.y = this.sinkFrom - 160 * k * k;
          if (this.deadAt >= 0 && Math.random() < dt * 6) {
            const fx = this.ctx.fx();
            toWorld(this.view, (Math.random() - 0.5) * 50, (Math.random() - 0.3) * 50, (Math.random() - 0.5) * 50, this.w1);
            fx?.explosion(this.v1.set(this.w1.x, this.w1.y - 160 * k * k, this.w1.z), 8, 3);
          }
          return;
        }
      }
      this.group.visible = false; this.live = false; this.deadAt = -1;
      return;
    }
    const e = f.primary;
    this.lastNet = net;
    // ease the tendril clock onto the server's (60 Hz ticks, wrapped at 2^16)
    const srv = net.age / 60;
    if (!this.live || Math.abs(srv - this.age) > 1.5) this.age = srv;
    else this.age += dt + (srv - this.age) * Math.min(1, dt * 2);
    this.live = true; this.lastSeen = time; this.sinkFrom = e.y;
    this.group.visible = true;
    this.group.position.set(e.x, e.y, e.z);
    this.group.rotation.set(0, e.yaw + Math.PI, 0);
    const v = this.fillView(f, net);
    if (!this.body || !this.limbs) return;
    const ph = v.ph;
    const tell = v.atk !== LAtk.None && ph === LPh.Tell;
    // ---- glow: eyes flare for every tell, the furnace roars with the jaw open, the gold core throbs
    const jaw = jawOpen(v);
    for (const m of this.mats.red) m.emissiveIntensity = tell ? 3.2 + Math.sin(time * 18) * 0.8 : 1.5 + Math.sin(time * 1.3) * 0.15;
    for (const m of this.mats.fire) m.emissiveIntensity = (0.85 + 0.15 * Math.sin(time * 2.1)) * (1 + jaw * 0.9);
    for (const m of this.mats.gold) m.emissiveIntensity = 0.75 + 0.35 * Math.sin(time * 6.5);
    // ---- eyes stare at the deck in front of it (the posse it has turned to face), with saccades
    for (let i = 0; i < 2; i++) {
      const eye = this.bone(i ? 'eye_R' : 'eye_L');
      if (!eye) continue;
      const ax = EYES[i].ax;
      this.v1.set(ax[0], ax[1], ax[2]);
      // on the deck it stations over (~74 m ahead, ~20 m down: model 49 / -13), wandering a little
      const p = EYES[i].p;
      this.v2.set(Math.sin(time * 0.31 + i * 0.4) * 6 - p[0], -13 + Math.sin(time * 0.43) * 2 - p[1], 49 - p[2]).normalize();
      this.q1.setFromUnitVectors(this.v1, this.v2);
      eye.quaternion.copy(this.q1).multiply(this.restQ(eye));
      // visor: shut while flinching, a slow heavy blink now and then
      const lid = this.bone(i ? 'lid_R' : 'lid_L');
      if (lid) {
        const blink = (time + i * 0.07) % 7.3 < 0.22 ? 1 : 0;
        const shut = (v.lids & (1 << i)) || blink ? 1 : 0;
        const cur = (lid.userData.k as number | undefined) ?? 0;
        const k = cur + (shut - cur) * Math.min(1, dt * (shut ? 22 : 6));
        lid.userData.k = k;
        lid.quaternion.copy(this.restQ(lid)).multiply(this.q2.setFromAxisAngle(X, k * 1.0));
      }
    }
    // ---- the jaw drops (+X), the maw turbine spins (local Y), harder with the jaw open
    const jb = this.bone('jaw');
    if (jb) jb.quaternion.copy(this.restQ(jb)).multiply(this.q2.setFromAxisAngle(X, jaw * 0.42 + Math.sin(time * 0.9) * 0.02));
    const mb = this.bone('maw');
    if (mb) mb.quaternion.copy(this.restQ(mb)).multiply(this.q2.setFromAxisAngle(Y, time * (0.6 + jaw * 5)));
    // ---- turrets: slow scan; rocket drums spin up through the barrage tell
    for (let i = 0; i < N_TURRETS; i++) {
      const rocket = i >= 4;
      const b = this.bone(rocket ? `turret_rocket_${i - 4}` : `turret_blaster_${i}`);
      if (!b) continue;
      if (!turretAlive(v.parts, i)) { b.scale.setScalar(0); continue; }
      b.scale.setScalar(1);
      const spin = rocket && v.atk === LAtk.Barrage && ph <= LPh.Exec ? time * 9 : Math.sin(time * 0.5 + i * 1.7) * 0.6;
      b.quaternion.copy(this.restQ(b)).multiply(this.q2.setFromAxisAngle(Y, spin));
    }
    for (let i = 0; i < N_BARN; i++) this.bone(`barnacle_${i}`)?.scale.setScalar(barnAlive(v.parts, i) ? 1 : 0);
    for (let i = 0; i < 4; i++) this.bone(`fx_wound_${i}`)?.scale.setScalar(v.skinned ? 1 + Math.sin(time * 5 + i) * 0.05 : 0);
    // ---- tendrils: the sim's joint rotations, straight onto the bones
    poseLimbs(v, this.quats);
    for (let i = 0; i < N_LIMBS; i++) {
      const alive = limbAlive(v.parts, i);
      this.bone(`panel_skirt_${LIMB_PANEL[i]}`)?.scale.setScalar(alive ? 1 : 0);
      const root = this.limbBone(i, SEG_NAMES[0]);
      if (!root) continue;
      const s = SOCKETS[i];
      root.position.set(s[0], s[1], s[2]);
      for (let k = 0; k < JOINTS; k++) {
        const b = this.limbBone(i, SEG_NAMES[k]);
        if (!b) continue;
        const o = (i * JOINTS + k) * 4;
        b.quaternion.set(this.quats[o], this.quats[o + 1], this.quats[o + 2], this.quats[o + 3]);
        if (k > 0) b.position.set(0, -SEG_OFF[k - 1], 0);
      }
      // a severed limb leaves a two-segment stump
      this.limbBone(i, SEG_NAMES[2])?.scale.setScalar(alive ? 1 : 0);
      // claws open through the slam's tell, clamp shut on the strike
      const slapping = v.atk === LAtk.Slap && v.limb === i;
      const open = slapping ? (ph === LPh.Tell ? v.prog : ph === LPh.Exec ? 1 - v.prog : 0) : 0.15 + 0.1 * Math.sin(time * 1.1 + i);
      for (let c = 0; c < 3; c++) {
        const cb = this.limbBone(i, `claw_${c}`);
        if (cb) cb.quaternion.copy(this.restQ(cb)).multiply(this.q2.setFromAxisAngle(X, -open * 0.8));
      }
    }
    // ---- the slam's strike ring, locked on the deck at the tell
    if (v.atk === LAtk.Slap && (ph === LPh.Tell || ph === LPh.Exec)) {
      const sd = this.strike;
      sd.mesh.visible = true;
      sd.mesh.position.set(v.tx, v.ty + 0.12, v.tz);
      sd.mesh.scale.set(13, 1, 13);
      sd.u.uK.value = ph === LPh.Tell ? v.prog : 1;
    }
  }

  bodyDied(_type: number, x: number, y: number, z: number, time: number) {
    this.deadAt = time;
    this.sinkFrom = y;
    this.ctx.sfx.play('cerberus_roar', undefined, { pitch: 0.42, volume: 1 });
    this.ctx.sfx.play('explosion', { x, y, z }, { pitch: 0.4 });
    this.ctx.flash(0.6); this.ctx.shake(0.9); this.ctx.rumble(1, 1, 900);
  }

  onEvent(kind: number, a: number, b: number, _eid: number, f: BossFrame | null) {
    const sfx = this.ctx.sfx, fx = this.ctx.fx();
    const net = (f?.net as LeviNet | undefined) ?? this.lastNet;
    const e = f?.primary;
    const pos = e ? { x: e.x, y: e.y, z: e.z } : undefined;
    switch (kind) {
      case BossBeat.Intro:
        if (a === 0) {
          // the cloud sea parts: a long sub-bass groan and a roll of thunder
          sfx.play('cerberus_roar', undefined, { pitch: 0.38, volume: 0.9 });
          sfx.play('boss_intro', undefined, { volume: 0.8 });
          this.ctx.rumble(0.7, 0.2, 1200); this.ctx.shake(0.35);
        } else {
          sfx.play('cerberus_roar', undefined, { pitch: 0.5, volume: 1 });
          this.ctx.flash(0.35); this.ctx.shake(0.5); this.ctx.rumble(0.9, 0.5, 700);
        }
        break;
      case BossBeat.Attack:
        if (a === LAtk.Slap) { sfx.play('brute_roar', pos, { pitch: 0.45, volume: 1 }); this.ctx.rumble(0.3, 0.2, 300); }
        else if (a === LAtk.Belch) sfx.play('cerberus_roar', undefined, { pitch: 0.55, volume: 0.85 });
        else sfx.play('service_klaxon', undefined, { pitch: 0.6, volume: 0.7 });
        break;
      case LeviEv.Volley:
        sfx.play(a === LAtk.Belch ? 'ground_pound' : 'bomb_throw', pos, { pitch: 0.5, volume: 1 });
        break;
      case LeviEv.Slam:
        if (net) {
          sfx.play('ground_pound', { x: net.tx, y: net.ty, z: net.tz }, { pitch: 0.55, volume: 1 });
          sfx.play('explosion', { x: net.tx, y: net.ty, z: net.tz }, { pitch: 0.5 });
          fx?.dust(this.v1.set(net.tx, net.ty, net.tz), 1.5);
        }
        this.ctx.shake(0.8); this.ctx.rumble(1, 0.8, 400);
        break;
      case LeviEv.Flinch:
        sfx.play('impact_armor', pos, { pitch: 0.5 });
        break;
      case LeviEv.PartLost: {
        // the blast at the part's world position (the pose is the last one drawn)
        let m: readonly number[] = SOCKETS[b] ?? [0, 0, 0];
        const v = this.view;
        if (a === LKind.Turret) {
          const t = this.bone(b >= 4 ? `turret_rocket_${b - 4}` : `turret_blaster_${b}`);
          if (t) m = [t.position.x, t.position.y, t.position.z];
        } else if (a === LKind.Barnacle) {
          const t = this.bone(`barnacle_${b}`);
          if (t) m = [t.position.x, t.position.y, t.position.z];
        }
        toWorld(v, m[0], m[1], m[2], this.w1);
        fx?.explosion(this.v1.set(this.w1.x, this.w1.y, this.w1.z), a === LKind.Limb ? 14 : 7, 3);
        sfx.play('explosion', this.w1, { pitch: a === LKind.Limb ? 0.45 : 0.7 });
        if (a === LKind.Limb) { sfx.play('cerberus_roar', undefined, { pitch: 0.48, volume: 1 }); this.ctx.shake(0.6); this.ctx.flash(0.3); }
        break;
      }
      case LeviEv.Skinned:
        sfx.play('boss_phase', undefined, { pitch: 0.7 }); this.ctx.shake(0.7); this.ctx.flash(0.4);
        break;
      case BossBeat.Dead:
        sfx.play('explosion', undefined, { pitch: 0.35 });
        break;
    }
  }

  ray(f: BossFrame, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, ap: number, out: BossAim) {
    out.t = -1; out.kind = 0; out.head = false; out.label = '';
    if (!f.primary) return out;
    const v = this.fillView(f, f.net as LeviNet);
    const h = leviRaycast(v, ox, oy, oz, dx, dy, dz, maxT, this.hit);
    if (h.t < 0) return out;
    out.t = h.t;
    out.kind = h.weak ? 3 : h.av > ap ? 4 : 2;
    out.head = h.weak;
    out.label = partLabel(v, h);
    return out;
  }

  /** No boss bar (§4b: a killable secret, no coaching); the aim label still names the part. */
  hud(_f: BossFrame, _aimLabel: string, _o: HudBoss) { return null; }

  objective(_f: BossFrame | null, o: HudObjective) {
    o.title = 'HOLD THE PLATFORM'; o.detail = 'THE TRAIN IS COMING';
  }

  dispose() {
    this.ctx.scene.remove(this.group);
    this.strike.dispose();
    for (const d of this.disposables) d.dispose();
    this.body?.meshes[0]?.skeleton.dispose();
    this.limbs?.meshes[0]?.skeleton.dispose();
  }
}

const X = new THREE.Vector3(1, 0, 0), Y = new THREE.Vector3(0, 1, 0);

export const leviathanClient: BossClientFactory = (ctx) => new LeviClient(ctx);
